import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useUserProfile } from '@/contexts/user.context';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/useToast';
import { UserRole, BackfillTaskResponse } from '@/lib/types';
import { backfillCampaignKeywordMetrics } from '@/lib/apis';
import { AlertCircle, CheckCircle2, Database } from 'lucide-react';

interface KeywordMetricsBackfillCardProps {
  campaignId: number;
}

export function KeywordMetricsBackfillCard({ campaignId }: KeywordMetricsBackfillCardProps) {
  const { user } = useUserProfile();
  const { toast } = useToast();
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillResponse, setBackfillResponse] = useState<BackfillTaskResponse | null>(null);

  // Guard clause - only render for admin users
  if (!user || user.role !== UserRole.ADMIN) {
    return null;
  }

  const handleBackfill = async () => {
    setIsBackfilling(true);
    setBackfillResponse(null);

    try {
      const response = await backfillCampaignKeywordMetrics(campaignId);
      setBackfillResponse(response);

      toast({
        title: 'Success',
        description: response.message || 'Keyword metrics backfill started successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start keyword metrics backfill',
        variant: 'destructive',
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <Card className='rounded-lg border bg-white'>
      <CardHeader>
        <CardTitle className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
          <Database className='h-5 w-5' />
          Admin: Keyword Metrics Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-gray-900'>
          Backfill reach and competitiveness metrics for keywords in this campaign that don't have metrics yet.
        </p>

        <Button onClick={handleBackfill} disabled={isBackfilling} className='w-full md:w-auto'>
          {isBackfilling && <Spinner className='mr-2 h-4 w-4' />}
          {isBackfilling ? 'Starting Backfill...' : 'Backfill Keyword Metrics'}
        </Button>

        {isBackfilling && (
          <div className='flex items-center justify-center p-6 bg-gray-50 rounded-lg'>
            <Spinner className='mr-3' />
            <p className='text-gray-900'>Starting backfill task...</p>
          </div>
        )}

        {backfillResponse && !isBackfilling && (
          <Alert variant='default'>
            <CheckCircle2 className='h-4 w-4' />
            <AlertTitle className='text-gray-900'>Backfill Task Started</AlertTitle>
            <AlertDescription className='text-gray-900'>
              <div className='space-y-2 mt-2'>
                <p>{backfillResponse.message}</p>
                <div className='grid grid-cols-2 gap-2 text-sm'>
                  <div>
                    <span className='font-medium'>Total Keywords:</span> {backfillResponse.total_keywords}
                  </div>
                  <div>
                    <span className='font-medium'>Needing Metrics:</span> {backfillResponse.keywords_needing_metrics}
                  </div>
                  <div>
                    <span className='font-medium'>Task ID:</span>{' '}
                    <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>{backfillResponse.task_id}</code>
                  </div>
                  <div>
                    <span className='font-medium'>Status:</span> {backfillResponse.status}
                  </div>
                </div>
                <div className='mt-4 bg-blue-50 p-3 rounded-md text-sm text-blue-900 border border-blue-100'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0' />
                    <p>
                      The backfill task is running in the background. Metrics will be populated asynchronously. This may
                      take a few minutes depending on the number of keywords.
                    </p>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
